import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "./DataTable";
import { jwtDecode } from 'jwt-decode';
import SWOP_LOGO from "../assets/swoplogo2.png";

const Dashboard = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [userPrivilege, setUserPrivilege] = useState("");

  // Decodes JWT token to retrieve user email
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserEmail(decoded.email);
        setUserPrivilege(decoded.privilege);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, []);

  // Fetch customer data from API
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsAuthenticated(false);
        navigate("/");
        return;
      }
      const response = await fetch("/api/Customers", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem("token");
        setIsAuthenticated(false);
        navigate("/");
        return;
      }
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      const data = await response.json();
      setData(data);
      setFilteredData(data);
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  }, [setIsAuthenticated, navigate]);

  // Download total hearts as CSV
  const handleHeartsDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsAuthenticated(false);
        navigate("/");
        return;
      }

      const response = await fetch("/api/getAllHearts", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();

      // Create CSV content
      const csvContent = `Total Hearts\n${data.totalHearts}`;

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', 'total_hearts.csv');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      setError(error.message);
      console.error("Error downloading hearts:", error);
    }
  };

  // Triggers data fetch on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handles user logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    setIsAuthenticated(false);
    navigate("/");
  };

  // Filters data based on search input
  const handleSearchChange = (e) => {
    localStorage.removeItem("previousPage");
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const queryWords = query.split(" ").filter((word) => word.trim() !== "");
    const filtered = data.filter((user) =>
      queryWords.every((word) =>
        Object.values(user)
          .filter((value) => value !== null)
          .some((value) => String(value).toLowerCase().includes(word))
      )
    );
    setFilteredData(filtered);
  };

  // Refresh data when header is clicked
  const handleHeaderClick = () => {
    setSearchQuery("");
    setLoading(true);
    fetchData();
  };

  // Resets page callback for datatable
  const resetPage = () => {};

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #b3e672 10%, #7ba02d 90%)' }}>
      <header>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Left side - Logout */}
            <div className="flex items-center space-x-12">
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Logout
              </button>
              <div className="flex flex-col">
                <span className="text-sm text-gray-600">Logged in as:</span>
                <span className="text-sm font-medium text-gray-900">{userEmail}</span>
              </div>
            </div>

            {/* Center - Logo and Title */}
            <div className="text-center flex-1 flex items-center justify-center space-x-4">
              <div className="relative inline-block">
                <img src={SWOP_LOGO} alt="Swop Logo" className="h-20 w-auto" />
                <div className="absolute inset-0 pointer-events-none"></div>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 cursor-pointer" onClick={handleHeaderClick}>
                  Customer Database
                </h1>
                <p className="text-sm text-gray-500">

                  </p>
              </div>  
            </div>

            {/* Right side - Buttons */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handleHeartsDownload}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Download Total Hearts
              </button>
              {userPrivilege === "admin" && (
                <button
                  onClick={() => navigate("/admin-list")}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Manage Admins
                </button>
              )}
              <button
                onClick={() => navigate("/add-customer")}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Add Customer
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="max-w-xs">
            <label htmlFor="search" className="sr-only">Search customers</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                id="search"
                value={searchQuery}
                onChange={handleSearchChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm select-none"
                placeholder="Search customers..."
              />
            </div>
          </div>
        </div>

        {/* DataTable */}
        <div className="bg-white shadow rounded-lg">
          <DataTable data={filteredData} onResetPage={resetPage} />
        </div>
      </main>
    </div>
  );
};

export default Dashboard;